/**
 * one-stop/config — defensive `block.config` readers + render-time formatters.
 *
 * MOVED HERE FROM `screens/portal/portal-config.ts` in milestone 2, unchanged in
 * behaviour, because the renderer package — not the portal screen — is what both
 * hosts import.
 *
 * WHY EVERY READ IS DEFENSIVE
 * `block.config` is authored settings passed through VERBATIM by the portal
 * worker; nothing on the read path runs the registry schema against it. So config
 * is an opaque `Record<string, unknown>` here and every read is: narrow the type,
 * take the documented default when the key is absent, never throw. This is also
 * what makes the unsupported-block path safe — a document may legally contain a
 * shipped canvas block (`text`, `hero_banner`, `email_*`) whose config has no
 * shape in common with anything below, and arbitrary config must never crash a
 * page.
 *
 * AUTHORED STRINGS ARE LOCALE MAPS (`{ 'en-US': '…' }`). Strings in `data` are
 * already resolved in the document's locale and are NEVER re-resolved here.
 *
 * REGISTRY DEFAULTS (one-stop-builder kit §5): the authoring path now stores the
 * PARSED document, so registry defaults materialize at save time and reach the
 * wire. The three fallbacks that used to invert the registry
 * (`showWalkMinutes`, `showOpenUntil`, `showCapabilities`) now MIRROR it — they
 * default to `true`, so the two sides cannot disagree again.
 *
 * MONEY AND TIME are formatted HERE, at render, from the typed wire values —
 * `PortalMoney` (minor units + ISO-4217) and `PortalZonedTime` (civil date +
 * civil time + IANA zone). Neither ever arrives preformatted.
 */
import type { PortalMoney, PortalZonedTime } from "@v0y4ge/api-contracts";
import type { Translate } from "./strings";

export type Config = Record<string, unknown>;

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

/* ── scalar reads ────────────────────────────────────────────────────── */

/**
 * An authored string: either a bare string or a locale map. Resolution order is
 * the requested locale, then its base language, then any regional variant of it,
 * then the single entry when the map has exactly one — a document authored only
 * in `en-US` must still render on an `es-US` page rather than showing a hole.
 */
export function configText(config: Config, key: string, locale: string): string | null {
  return resolveAuthored(config[key], locale);
}

function resolveAuthored(raw: unknown, locale: string): string | null {
  if (typeof raw === "string") return raw.trim() || null;
  if (!isRecord(raw)) return null;
  const base = locale.split("-")[0] ?? locale;
  const candidates = [locale, base, ...Object.keys(raw).filter((k) => k.startsWith(`${base}-`))];
  for (const k of candidates) {
    const v = raw[k];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  const entries = Object.values(raw).filter((v): v is string => typeof v === "string" && !!v.trim());
  return entries.length === 1 ? entries[0]!.trim() : null;
}

/** Same resolution, over a nested config object (`config.signedOut.title`). */
export function nestedConfigText(
  config: Config,
  path: readonly [string, string],
  locale: string,
): string | null {
  const nested = config[path[0]];
  return isRecord(nested) ? configText(nested, path[1], locale) : null;
}

/** The first of several alias keys that resolves to an authored string. */
export function configTextAny(config: Config, keys: readonly string[], locale: string): string | null {
  for (const key of keys) {
    const value = configText(config, key, locale);
    if (value) return value;
  }
  return null;
}

export function configBool(config: Config, key: string, fallback: boolean): boolean {
  const raw = config[key];
  return typeof raw === "boolean" ? raw : fallback;
}

export function nestedConfigBool(
  config: Config,
  path: readonly [string, string],
  fallback: boolean,
): boolean {
  const nested = config[path[0]];
  return isRecord(nested) ? configBool(nested, path[1], fallback) : fallback;
}

export function configString(config: Config, key: string, fallback: string): string {
  const raw = config[key];
  return typeof raw === "string" && raw.trim() ? raw.trim() : fallback;
}

export function configInt(config: Config, key: string, fallback: number | null): number | null {
  const raw = config[key];
  return typeof raw === "number" && Number.isFinite(raw) ? Math.trunc(raw) : fallback;
}

/** A plain (non-locale-map) id, read from the first alias key that carries one. */
export function configId(config: Config, keys: readonly string[]): string | null {
  for (const key of keys) {
    const raw = key.includes(".") ? readPath(config, key) : config[key];
    if (typeof raw === "string" && raw.trim()) return raw.trim();
    if (Array.isArray(raw)) {
      const first = raw.find((v): v is string => typeof v === "string" && !!v.trim());
      if (first) return first.trim();
    }
  }
  return null;
}

function readPath(config: Config, path: string): unknown {
  let cursor: unknown = config;
  for (const segment of path.split(".")) {
    if (!isRecord(cursor)) return undefined;
    cursor = cursor[segment];
  }
  return cursor;
}

/* ── list reads ──────────────────────────────────────────────────── */

function entries(config: Config, key: string): Record<string, unknown>[] {
  const raw = config[key];
  return Array.isArray(raw) ? raw.filter(isRecord) : [];
}

/** `config.chips` on the search block: `[{ label, query? }]`, both locale maps. */
export interface SearchChip {
  readonly label: string;
  readonly query: string;
}

export function configChips(config: Config, locale: string, max: number | null): SearchChip[] {
  const chips: SearchChip[] = [];
  for (const entry of entries(config, "chips")) {
    const label = configText(entry, "label", locale);
    if (!label) continue;
    // A chip with no authored query searches its own label — the documented
    // fallback, and why a label-only chip must read like something a student
    // would actually type (one-stop-builder kit §4.4b).
    chips.push({ label, query: configText(entry, "query", locale) ?? label });
  }
  return max !== null && max > 0 ? chips.slice(0, max) : chips;
}

/**
 * `config.linkGroups` on help-contact: `[{ title, links: [{ label, target }] }]`.
 * `target` is REQUIRED by the registry and its shape is the authoring lane's;
 * `hrefOf` below knows all of the shapes it comes in, and a link whose target
 * resolves to nothing renders as a plain label — a link with no reachable target
 * is a label, not a broken anchor.
 */
export interface HelpLink {
  readonly label: string;
  readonly href: string | null;
}
export interface HelpLinkGroup {
  readonly title: string | null;
  readonly links: readonly HelpLink[];
}

export function configLinkGroups(config: Config, locale: string): HelpLinkGroup[] {
  const groups: HelpLinkGroup[] = [];
  for (const entry of entries(config, "linkGroups")) {
    const links: HelpLink[] = [];
    const rawLinks = Array.isArray(entry["links"]) ? entry["links"].filter(isRecord) : [];
    for (const link of rawLinks) {
      const label = configText(link, "label", locale);
      if (!label) continue;
      links.push({ label, href: hrefOf(link["target"] ?? link["href"] ?? link["url"], locale) });
    }
    groups.push({ title: configText(entry, "title", locale), links });
  }
  return groups;
}

/**
 * The target an authored link points at, as a plain string — before the safety
 * filter, which `hrefOf` applies.
 *
 * FOUR SHAPES REACH THIS AND ONLY THREE USED TO RESOLVE: a bare string, an
 * object carrying `url` or `href`, a locale map under either of those, and the
 * authoring lane's DEEP-LINK target, `{ kind: 'deep-link', route, params }`.
 * That fourth one is what the footer's links, `events.allEventsTarget`,
 * `context-banner.link`, `notice.link`, `button.target` and `social.links[]`
 * actually carry, and `route` was never read — so every authored link on the
 * page resolved to null and rendered as text, and a content `button` pointing at
 * one rendered disabled saying it had not been pointed at anything (CC 0020
 * finding 6). `route` is read now, through the SAME filter as everything else.
 *
 * ⛔ A TARGET CARRYING NON-EMPTY `params` DOES NOT RESOLVE, and that is the
 * honest answer rather than a missing feature. The pair has no declared
 * serialization — `{ id: '42' }` beside `/events` is a path segment in one
 * reading and a query string in another — and a link that silently drops them
 * points somewhere the author did not choose, which is worse than a label.
 * Composing that pair is a contract question (JAI-1175), not a guess to make
 * here; the day the resolver flattens a target before it reaches us, this
 * branch is simply never taken.
 */
function targetValue(raw: Record<string, unknown>, locale: string): string | null {
  const direct =
    (typeof raw["url"] === "string" && raw["url"]) ||
    (typeof raw["href"] === "string" && raw["href"]) ||
    resolveAuthored(raw["url"] ?? raw["href"], locale);
  if (typeof direct === "string" && direct) return direct;
  const params = raw["params"];
  if (isRecord(params) && Object.keys(params).length > 0) return null;
  return resolveAuthored(raw["route"], locale);
}

/**
 * A safe href, or null. Only http(s), mailto, tel and same-origin paths — a
 * route that is neither an absolute URL nor a `/`-rooted path is still no
 * target, whichever key it arrived under, and still renders as text.
 */
export function hrefOf(raw: unknown, locale: string): string | null {
  const value = typeof raw === "string" ? raw : isRecord(raw) ? targetValue(raw, locale) : null;
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (/^(?:https?:\/\/|mailto:|tel:)/iu.test(trimmed)) return trimmed;
  if (trimmed.startsWith("/") && !trimmed.startsWith("//")) return trimmed;
  return null;
}

/** `config.items` on faq: `[{ question, answer }]`, both locale maps. */
export interface FaqItem {
  readonly question: string;
  readonly answer: string | null;
}

export function configFaqItems(config: Config, locale: string): FaqItem[] {
  const items: FaqItem[] = [];
  for (const entry of [...entries(config, "items"), ...entries(config, "questions")]) {
    const question = configTextAny(entry, ["question", "title", "label"], locale);
    if (!question) continue;
    items.push({ question, answer: configTextAny(entry, ["answer", "body", "text"], locale) });
  }
  return items;
}

/** `config.items` on stats: `[{ value, label }]`. `value` may be a plain number. */
export interface StatItem {
  readonly value: string;
  readonly label: string | null;
}

export function configStats(config: Config, locale: string): StatItem[] {
  const items: StatItem[] = [];
  for (const entry of [...entries(config, "items"), ...entries(config, "stats")]) {
    const raw = entry["value"];
    const value =
      typeof raw === "number" && Number.isFinite(raw)
        ? new Intl.NumberFormat(locale).format(raw)
        : configTextAny(entry, ["value", "figure", "number"], locale);
    if (!value) continue;
    items.push({ value, label: configTextAny(entry, ["label", "caption", "title"], locale) });
  }
  return items;
}

/** `config.links` on social: `[{ label, url, platform }]`. */
export interface SocialLink {
  readonly label: string;
  readonly href: string | null;
}

export function configSocialLinks(config: Config, locale: string): SocialLink[] {
  const links: SocialLink[] = [];
  for (const entry of [...entries(config, "links"), ...entries(config, "items")]) {
    const label = configTextAny(entry, ["label", "platform", "network", "title"], locale);
    if (!label) continue;
    links.push({ label, href: hrefOf(entry["url"] ?? entry["href"] ?? entry["target"], locale) });
  }
  return links;
}

/* ── formatters ────────────────────────────────────────────────── */

/**
 * `PortalMoney` → a string in the VIEWER's locale. The minor-unit exponent comes
 * from `Intl` for the currency itself, never a hardcoded /100 — JPY has none and
 * would render 42350× too large.
 */
export function formatMoney(money: PortalMoney, locale: string): string {
  const fmt = new Intl.NumberFormat(locale, { style: "currency", currency: money.currency });
  const digits = fmt.resolvedOptions().maximumFractionDigits ?? 2;
  return fmt.format(money.amountMinor / 10 ** digits);
}

function asUtcDate(when: PortalZonedTime): Date | null {
  const date = new Date(`${when.date}T${when.time}:00Z`);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * A civil date+time in its OWN zone, rendered as authored. The parts are read
 * from the civil strings and formatted through a UTC-pinned `Intl` call, so the
 * viewer's zone never shifts a published opening; the zone is named alongside
 * when it differs from the viewer's, because "2:30 PM" is ambiguous otherwise.
 */
export function formatZonedTime(
  when: PortalZonedTime,
  locale: string,
  opts: { withDate?: boolean; tenantTimeZone?: string } = {},
): string {
  const asUtc = asUtcDate(when);
  if (!asUtc) return `${when.date} ${when.time}`;
  const time = new Intl.DateTimeFormat(locale, {
    hour: "numeric",
    minute: "2-digit",
    timeZone: "UTC",
  }).format(asUtc);
  const date = opts.withDate
    ? new Intl.DateTimeFormat(locale, {
        weekday: "short",
        month: "short",
        day: "numeric",
        timeZone: "UTC",
      }).format(asUtc)
    : null;
  const viewerZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const zone = when.timeZone !== viewerZone ? shortZoneLabel(when.timeZone) : null;
  return [date, time, zone].filter(Boolean).join(" · ");
}

/** The month/day pair for an event row's date tile, in the event's own zone. */
export function dateTileParts(when: PortalZonedTime, locale: string): { mon: string; day: string } {
  const asUtc = asUtcDate(when);
  if (!asUtc) return { mon: "", day: when.date.slice(-2) };
  return {
    mon: new Intl.DateTimeFormat(locale, { month: "short", timeZone: "UTC" }).format(asUtc),
    day: new Intl.DateTimeFormat(locale, { day: "numeric", timeZone: "UTC" }).format(asUtc),
  };
}

/** Weekday + time, the prototype's event line ("Mon 10:00 AM"). */
export function formatWeekdayTime(when: PortalZonedTime, locale: string): string {
  const asUtc = asUtcDate(when);
  if (!asUtc) return `${when.date} ${when.time}`;
  const weekday = new Intl.DateTimeFormat(locale, { weekday: "short", timeZone: "UTC" }).format(asUtc);
  const time = new Intl.DateTimeFormat(locale, {
    hour: "numeric",
    minute: "2-digit",
    timeZone: "UTC",
  }).format(asUtc);
  return `${weekday} ${time}`;
}

function shortZoneLabel(timeZone: string): string {
  try {
    const parts = new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "short" }).formatToParts(
      new Date(),
    );
    return parts.find((p) => p.type === "timeZoneName")?.value ?? timeZone;
  } catch {
    return timeZone;
  }
}

/**
 * Whole minutes, in words the surface uses everywhere — and in the DOCUMENT's
 * language. The unit words used to be English literals right here: the JSX scan
 * for hardcoded copy passes over a formatter, which is exactly how `min` / `hr`
 * survived JAI-1154 on an `es-US` page. They are catalog keys now, so the unit
 * comes from the same place every other word on the page does.
 */
export function formatMinutes(minutes: number, locale: string, t: Translate): string {
  const n = new Intl.NumberFormat(locale);
  const min = t("queues.minutes");
  const hr = t("queues.hours");
  if (minutes < 60) return `${n.format(minutes)} ${min}`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${n.format(h)} ${hr}` : `${n.format(h)} ${hr} ${n.format(m)} ${min}`;
}

/** A UTC instant as a clock time in the viewer's own zone — hold countdowns. */
export function formatInstantTime(instant: string, locale: string): string {
  const at = new Date(instant);
  if (Number.isNaN(at.getTime())) return "";
  return new Intl.DateTimeFormat(locale, { hour: "numeric", minute: "2-digit" }).format(at);
}
